/** @jsx jsx */
import React, { useState, type PropsWithChildren, memo } from 'react';

import { jsx } from '@compiled/react';
import { token } from '@atlaskit/tokens';

import PermalinkProvider from '@atlassian/jira-issue-view-common/src/component/permalink-button/permalink-provider/index.tsx';
import { ModalContextProvider as EntrypointModalContextProvider } from '@atlassian/entry-point-modal-context/ModalContextProvider';
import { SERVICE_DESK_PROJECT } from '@atlassian/jira-common-constants/src/index.tsx';
import { InternalCommentContainer } from './styled.tsx';
import CommentComponent from './custom-comment/CommentComponent.tsx';
import CommentContextProvider, {
	useCommentContext,
} from './custom-comment/CommentContextProvider.tsx';
import { localStorageProvider, UNREAD_COMMENT_LOZENGE_VIEWED_KEY } from './constants.tsx';
import type { ComponentProps as Props } from './types.tsx';
import CommentContainer, {
	FloatingMenuContainer,
	OptimisticCommentContainer,
	ThreadedCommentContainer,
} from './custom-comment/CommentContainer.tsx';
import {
	useFireHiglightedCommentAnalytics,
	useReplyToComment,
	useScrollCommentIntoView,
	useSmoothScrollIntoView,
	useUnreadCommentLozengeState,
} from './custom-comment/utils.tsx';
import { CommentBase as CommentBaseLegacy } from './legacy-view.tsx';

const RenderOptimisticComment = ({ children }: PropsWithChildren) => {
	const { permalink, hasFloatingMenu, isThreadedCommentsEnabled } = useCommentContext();
	if (hasFloatingMenu && isThreadedCommentsEnabled) {
		return <FloatingMenuContainer permalink={permalink}>{children}</FloatingMenuContainer>;
	}
	if (isThreadedCommentsEnabled) {
		return <ThreadedCommentContainer permalink={permalink}>{children}</ThreadedCommentContainer>;
	}
	return <OptimisticCommentContainer permalink={permalink}>{children}</OptimisticCommentContainer>;
};

const WithInternalCommentContainer = ({ children }: PropsWithChildren) => {
	const { isEditing, isHighlighted, isInternal } = useCommentContext();
	const highlightColor = isHighlighted
		? token('color.background.accent.yellow.subtler')
		: token('color.background.accent.yellow.subtlest');
	if (!isInternal) {
		return children;
	}
	return (
		<InternalCommentContainer isEditing={isEditing} highlightColor={highlightColor}>
			{children}
		</InternalCommentContainer>
	);
};

const CommentRender = ({ children }: PropsWithChildren) => {
	const { isOptimistic, permalink } = useCommentContext();

	if (isOptimistic) {
		return <RenderOptimisticComment>{children}</RenderOptimisticComment>;
	}
	return <CommentContainer permalink={permalink}>{children}</CommentContainer>;
};
const CommentBaseInner = ({ children }: PropsWithChildren) => {
	const { isThreadedCommentsEnabled } = useCommentContext();
	useScrollCommentIntoView();
	useUnreadCommentLozengeState();
	useSmoothScrollIntoView();
	useFireHiglightedCommentAnalytics();
	useReplyToComment();

	return (
		<WithInternalCommentContainer>
			<CommentRender>
				<CommentComponent>{isThreadedCommentsEnabled && children}</CommentComponent>
			</CommentRender>
		</WithInternalCommentContainer>
	);
};

export const CommentBase = memo((props: Props) => {
	const [selectedEventOccurredAt, setSelectedEventOccuredAt] = useState(props.eventOccurredAt);
	const [showNewUnreadCommentLozenge, setShowNewUnreadCommentLozenge] = useState(
		!localStorageProvider.get(UNREAD_COMMENT_LOZENGE_VIEWED_KEY) &&
			props.commentId === props.topMostUnreadCommentId &&
			!props.isActivityFeedInViewPort,
	);
	const [optimisticCommentContainer, setOptimisticCommentPermalinkContainer] = useState<
		HTMLElement | null | undefined
	>(undefined);
	const [unreadCommentRef, updateUnreadCommentRef] = useState<HTMLElement | null | undefined>(
		undefined,
	);
	const [commentPermalinkContainer, setCommentPermalinkContainer] = useState<
		HTMLElement | null | undefined
	>(undefined);

	if (props.hasFloatingMenu || props.projectType === SERVICE_DESK_PROJECT) {
		return <CommentBaseLegacy {...props} />;
	}
	return (
		<EntrypointModalContextProvider>
			<PermalinkProvider>
				{(permalink) => (
					<CommentContextProvider
						setShowNewUnreadCommentLozenge={setShowNewUnreadCommentLozenge}
						optimisticCommentContainer={optimisticCommentContainer}
						setOptimisticCommentContainer={setOptimisticCommentPermalinkContainer}
						commentPermalinkContainer={commentPermalinkContainer}
						setCommentPermalinkContainer={setCommentPermalinkContainer}
						unreadCommentElement={unreadCommentRef}
						hasChildren={!!props.children}
						restrictedToText="" // this props can be removed once we clean up fg jira_custom_comment_component_refactor
						showNewUnreadCommentLozenge={showNewUnreadCommentLozenge}
						setUnreadCommentScrollRef={updateUnreadCommentRef}
						permalink={permalink}
						selectedEventOccurredAt={selectedEventOccurredAt}
						setSelectedEventOccuredAt={setSelectedEventOccuredAt}
						{...props}
					>
						<CommentBaseInner>{props.children}</CommentBaseInner>
					</CommentContextProvider>
				)}
			</PermalinkProvider>
		</EntrypointModalContextProvider>
	);
});
